import { runBootstrapCheck } from '@/lib/bootstrap-diagnostics/bootstrap-diagnostics';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useEnterAuth, type EnterAuthUser } from '@enter/auth';
import {
    assertSuccessfulEnvelope,
    useUsersApi,
    type AccountCenterInfo,
    type ApiUserInfo,
} from '@enter/api';
import { classifyUserInfoFailure, type UserInfoLoginAttempt } from '@/features/account/user-info/login-attempt';

export type UserInfoStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UserInfoContextValue {
    /** Code API 返回的当前用户。 */
    userInfo: ApiUserInfo['user'] | null;
    /** Account Center 所需的顶层用户字段。 */
    accountCenterInfo: AccountCenterInfo | null;
    /** 当前用户 snapshot 的请求状态。 */
    status: UserInfoStatus;
    /** 首次请求或最近一次 refresh 的错误。 */
    error: Error | null;
    /** Auth0 claim 标记的新用户。 */
    isNewUser: boolean;
    /** 首次进入应用时捕获的登录链路 trace id，由独立 User Effect 消费。 */
    authTraceId: string | null;
    /** 加载当前 principal 的用户 snapshot；已有成功 snapshot 时直接返回。 */
    ensureUserInfo: () => Promise<ApiUserInfo['user']>;
    /** 显式重新读取当前用户；已有 snapshot 时刷新失败不会清空旧数据。 */
    refreshUserInfo: () => Promise<ApiUserInfo['user']>;
    /** 兼容用户资料 mutation 的本地提交；不承担任何埋点或第三方副作用。 */
    setUserInfo: (userInfo: ApiUserInfo['user'] | null) => void;
    /** Auth0 基础资料，仅用于 API 用户资料缺失字段的展示 fallback。 */
    auth0User: EnterAuthUser | undefined;
    /** 兼容既有消费点；等价于 `status === 'loading'`。 */
    isLoading: boolean;
}

interface UserInfoProviderProps {
    /** 应用子树。 */
    children: ReactNode;
}

interface UserInfoProviderScopeProps extends UserInfoProviderProps {
    /** 当前认证 principal；作为 session 内存边界。 */
    userScopeId: string | null;
    /** Auth0 基础用户字段。 */
    auth0User: EnterAuthUser | undefined;
    /** Auth session 是否仍在解析。 */
    authLoading: boolean;
    /** URL 首次捕获的 auth trace id。 */
    authTraceId: string | null;
}

interface UserInfoState {
    userInfo: ApiUserInfo['user'] | null;
    accountCenterInfo: AccountCenterInfo | null;
    status: UserInfoStatus;
    error: Error | null;
    loginAttempt: UserInfoLoginAttempt | null;
}

interface UserInfoRequest {
    controller: AbortController;
    promise: Promise<ApiUserInfo['user']>;
}

const UserInfoContext = createContext<(UserInfoContextValue & { loginAttempt: UserInfoLoginAttempt | null }) | undefined>(undefined);

function normalizeError(error: unknown): Error {
    return error instanceof Error ? error : new Error('Failed to fetch user information');
}

function getAccountCenterInfo(response: ApiUserInfo): AccountCenterInfo {
    const user = response.user;
    return {
        global_uid: user.global_uid ?? '',
        email_verified: user.email_verified ?? false,
        email_verified_at: user.email_verified_at ?? null,
        must_verify_email: response.must_verify_email === true,
        merge_candidate_id: response.merge_candidate_id ?? null,
        merge_action: response.merge_action ?? 'no_action',
        merge_block_reason: response.merge_block_reason ?? null,
        masked_email: response.masked_email ?? '',
        merge_candidate_product_id: response.merge_candidate_product_id ?? null,
        merge_candidate_product_name: response.merge_candidate_product_name ?? null,
        email_verification_reason: response.email_verification_reason ?? null,
    };
}

function UserInfoProviderScope({
    auth0User,
    authLoading,
    authTraceId,
    children,
    userScopeId,
}: UserInfoProviderScopeProps) {
    const usersApi = useUsersApi();
    const [bootstrapStart] = useState(() => ({ startedAtMS: Date.now(), startedMonoMS: performance.now() }));
    const [state, setState] = useState<UserInfoState>(() => ({
        userInfo: null,
        accountCenterInfo: null,
        status: userScopeId ? 'idle' : 'success',
        error: null,
        loginAttempt: null,
    }));
    const stateRef = useRef(state);
    const requestRef = useRef<UserInfoRequest | null>(null);
    const automaticRequestStartedRef = useRef(false);
    const mountedRef = useRef(true);

    const commitState = useCallback((nextState: UserInfoState) => {
        if (!mountedRef.current) return;
        stateRef.current = nextState;
        setState(nextState);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            queueMicrotask(() => {
                // StrictMode 会立即重放 effect；只在真正卸载 scope 后取消请求。
                if (!mountedRef.current) {
                    requestRef.current?.controller.abort();
                    requestRef.current = null;
                }
            });
        };
    }, []);

    const loadUserInfo = useCallback(
        (force: boolean): Promise<ApiUserInfo['user']> => {
            if (!userScopeId) {
                return Promise.reject(
                    new Error('User information requires an authenticated principal')
                );
            }

            const currentState = stateRef.current;
            if (!force && currentState.status === 'success' && currentState.userInfo) {
                return Promise.resolve(currentState.userInfo);
            }
            if (requestRef.current) {
                return requestRef.current.promise;
            }

            const controller = new AbortController();
            commitState({
                ...currentState,
                status: currentState.userInfo ? 'success' : 'loading',
                error: null,
            });

            const request = runBootstrapCheck(
                'user_info',
                async attempt => {
                    const observed = !currentState.userInfo;
                    const started: UserInfoLoginAttempt = {
                        id: attempt.id,
                        ...(currentState.loginAttempt === null ? bootstrapStart
                            : { startedAtMS: Date.now(), startedMonoMS: performance.now() }),
                        phase: 'loading',
                    };
                    if (observed) commitState({ ...stateRef.current, loginAttempt: started });
                    let invalidPayload = false;
                    try {
                        const response = await usersApi.getUserInfo(controller.signal, {
                            headers: { 'X-Trace-ID': attempt.id },
                        });
                        attempt.responseReceived = true;
                        const successfulResponse = assertSuccessfulEnvelope(response);
                        const payload = successfulResponse.data;
                        if (!payload?.user) {
                            invalidPayload = true;
                            throw new Error(
                                successfulResponse.message || 'User information is unavailable'
                            );
                        }

                        let loginAttempt = stateRef.current.loginAttempt;
                        if (observed && !controller.signal.aborted) {
                            const userId = payload.user.user_id;
                            loginAttempt = Number.isSafeInteger(userId) && userId > 0
                                ? { ...started, phase: 'ready' }
                                : { ...started, phase: 'failed', finishedMonoMS: performance.now(),
                                    result: { outcome: 'technical_failure', reason: 'user_info_invalid_response' } };
                        }
                        commitState({
                            ...stateRef.current,
                            userInfo: payload.user,
                            accountCenterInfo: getAccountCenterInfo(payload),
                            status: 'success',
                            error: null,
                            loginAttempt,
                        });
                        return payload.user;
                    } catch (error) {
                        if (observed && !controller.signal.aborted && stateRef.current.loginAttempt?.phase !== 'failed') {
                            commitState({ ...stateRef.current, loginAttempt: {
                                ...started, phase: 'failed', finishedMonoMS: performance.now(),
                                result: invalidPayload
                                    ? { outcome: 'technical_failure', reason: 'user_info_invalid_response' }
                                    : classifyUserInfoFailure(error),
                            } });
                        }
                        throw error;
                    }
                },
                controller.signal
            )
                .catch(error => {
                    const normalizedError = normalizeError(error);
                    const latestState = stateRef.current;
                    if (!controller.signal.aborted) {
                        commitState({
                            ...latestState,
                            status: latestState.userInfo ? 'success' : 'error',
                            error: normalizedError,
                        });
                    }
                    throw normalizedError;
                })
                .finally(() => {
                    if (requestRef.current?.controller === controller) {
                        requestRef.current = null;
                    }
                });

            requestRef.current = { controller, promise: request };
            return request;
        },
        [bootstrapStart, commitState, userScopeId, usersApi]
    );

    const ensureUserInfo = useCallback(() => loadUserInfo(false), [loadUserInfo]);
    const refreshUserInfo = useCallback(() => loadUserInfo(true), [loadUserInfo]);

    useEffect(() => {
        if (!authLoading && userScopeId && !automaticRequestStartedRef.current) {
            automaticRequestStartedRef.current = true;
            void ensureUserInfo().catch(() => undefined);
        }
    }, [authLoading, ensureUserInfo, userScopeId]);

    const setUserInfo = useCallback(
        (userInfo: ApiUserInfo['user'] | null) => {
            const currentState = stateRef.current;
            commitState({
                ...currentState,
                userInfo,
                status: userInfo ? 'success' : userScopeId ? 'idle' : 'success',
                error: userInfo ? null : currentState.error,
            });
        },
        [commitState, userScopeId]
    );

    const value = useMemo<UserInfoContextValue & { loginAttempt: UserInfoLoginAttempt | null }>(
        () => ({
            userInfo: state.userInfo,
            accountCenterInfo: state.accountCenterInfo,
            status: state.status,
            error: state.error,
            isNewUser: auth0User?.isNewUser === true,
            authTraceId,
            ensureUserInfo,
            refreshUserInfo,
            setUserInfo,
            auth0User,
            isLoading: state.status === 'loading',
            loginAttempt: state.loginAttempt,
        }),
        [auth0User, authTraceId, ensureUserInfo, refreshUserInfo, setUserInfo, state]
    );

    return <UserInfoContext.Provider value={value}>{children}</UserInfoContext.Provider>;
}

function readInitialAuthTraceId(): string | null {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('auth_trace_id');
}

interface AuthTraceState {
    /** URL 首次捕获的 auth trace id；会话变为匿名后清空。 */
    id: string | null;
    /** 第一个认证 principal 认领 trace id，之后切换的账号不再携带。 */
    ownerScopeId: string | null;
}

export function UserInfoProvider({ children }: UserInfoProviderProps) {
    const {
        isAuthenticated,
        isLoading: authLoading,
        status: authStatus,
        user: auth0User,
    } = useEnterAuth();
    const [authTrace, setAuthTrace] = useState<AuthTraceState>(() => ({
        id: readInitialAuthTraceId(),
        ownerScopeId: null,
    }));
    const userScopeId = isAuthenticated ? (auth0User?.sub ?? null) : null;

    // 在渲染中按当前认证状态调整 trace 归属，不在渲染期间读写 ref。
    if (authStatus === 'anonymous') {
        if (authTrace.id !== null || authTrace.ownerScopeId !== null) {
            setAuthTrace({ id: null, ownerScopeId: null });
        }
    } else if (userScopeId && !authTrace.ownerScopeId) {
        setAuthTrace({ ...authTrace, ownerScopeId: userScopeId });
    }
    const authTraceId =
        userScopeId && authTrace.ownerScopeId === userScopeId ? authTrace.id : null;

    return (
        <UserInfoProviderScope
            key={userScopeId ?? 'anonymous'}
            auth0User={auth0User}
            authLoading={authLoading}
            authTraceId={authTraceId}
            userScopeId={userScopeId}
        >
            {children}
        </UserInfoProviderScope>
    );
}

export function useUserInfo(): UserInfoContextValue {
    const context = useContext(UserInfoContext);
    if (!context) {
        throw new Error('useUserInfo must be used within a UserInfoProvider');
    }
    return context;
}

/** 当前 principal 首次成功资料前的启动尝试；沿用请求编号，后续 refresh 不改写。 */
export function useUserInfoLoginAttempt(): UserInfoLoginAttempt | null {
    const context = useContext(UserInfoContext);
    if (!context) throw new Error('useUserInfoLoginAttempt must be used within a UserInfoProvider');
    return context.loginAttempt;
}
